// src/components/StylistConsultationView.jsx
//
// Aligned with current Esdras architecture (April 2026):
//   • Reads from biometric_history collection (written by renderHair in index.js)
//   • 3D viewer uses GLB models from Rodin (headScan / initialRenderUrl)
//   • No StyleSnap references — style try-ons are in users/{uid}/customStyles
//   • ModelLoader split into three separate top-level components (Rules of Hooks)
//   • Stylist notes committed to biometric_history document
//   • Longitudinal follicle density chart with alert threshold

import React, { useState, useEffect, Suspense } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, useFBX } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';

// ─────────────────────────────────────────────────────────────────────────────
//  MODEL LOADERS — three separate components (no conditional hooks)
// ─────────────────────────────────────────────────────────────────────────────
function OBJModel({ url, scale }) {
  const obj = useLoader(OBJLoader, url);
  return obj ? <primitive object={obj} scale={scale} /> : null;
}

function FBXModel({ url, scale }) {
  const fbx = useFBX(url);
  return fbx ? <primitive object={fbx} scale={scale} /> : null;
}

function GLTFModel({ url, scale, isCranial }) {
  const { scene } = useGLTF(url);
  if (!scene) return null;
  if (isCranial) {
    scene.traverse(child => {
      if (child.isMesh) {
        child.material = child.material.clone();
        child.material.color.set(GOLD);
        child.material.metalness = 0.85;
        child.material.roughness = 0.15;
      }
    });
  }
  return <primitive object={scene} scale={scale} />;
}

function ModelLoader({ url, scale = 1, isCranial = false }) {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.endsWith('.obj')) return <OBJModel url={url} scale={scale} />;
  if (lower.endsWith('.fbx')) return <FBXModel url={url} scale={scale} />;
  return                             <GLTFModel url={url} scale={scale} isCranial={isCranial} />;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CUSTOM TOOLTIP for the chart
// ─────────────────────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#050a0f', border:`1px solid ${GOLD}`, borderRadius:'10px', padding:'10px 14px' }}>
      <p style={{ color:GOLD, fontWeight:'900', fontSize:'0.65rem', margin:'0 0 4px', letterSpacing:'1px' }}>{label}</p>
      <p style={{ color:'#fff', fontSize:'0.8rem', margin:0 }}>
        Density: <strong style={{ color: payload[0].value < 40 ? '#ff4444' : GOLD }}>{payload[0].value.toFixed(1)}%</strong>
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function StylistConsultationView({ clientId }) {
  const [history,      setHistory]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);
  const [note,         setNote]         = useState('');
  const [isSaving,     setIsSaving]     = useState(false);

  useEffect(() => {
    if (!clientId) return;
    const fetch = async () => {
      try {
        const q = query(
          collection(db, 'biometric_history'),
          where('userId', '==', clientId),
          orderBy('createdAt', 'asc')
        );
        const snap = await getDocs(q);
        const data = snap.docs.map(d => {
          const entry = d.data();
          const hv    = entry.biometricData?.healthVector || {};
          return {
            id:           d.id,
            ...entry,
            // Formatted date for chart X axis
            date:         entry.createdAt?.toDate().toLocaleDateString('en-GB', { day:'numeric', month:'short' }) || 'N/A',
            // Head scan GLB URL — written by renderHair in index.js
            headModelUrl: entry.biometricData?.fashionVector?.cranialModelRef
                          || entry.renderUrl
                          || null,
            // Density score as percentage
            score:        Math.round((hv.follicleDensityScore || 0) * 100),
            // Individual health vectors
            hv,
          };
        });

        setHistory(data);
        if (data.length > 0) {
          const latest = data[data.length - 1];
          setSelectedScan(latest);
          setNote(latest.stylistNotes || '');
        }
      } catch (err) {
        console.error('Consultation fetch error:', err);
        toast.error('Failed to load biometric history');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [clientId]);

  const handleSaveNote = async () => {
    if (!selectedScan || isSaving) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'biometric_history', selectedScan.id), {
        stylistNotes:     note,
        notesUpdatedAt:   new Date(),
      });
      setHistory(prev => prev.map(item =>
        item.id === selectedScan.id ? { ...item, stylistNotes: note } : item
      ));
      toast.success('Notes committed to biometric vault');
    } catch {
      toast.error('Failed to save notes');
    } finally { setIsSaving(false); }
  };

  const handleChartClick = (e) => {
    if (e?.activePayload?.[0]) {
      const clicked = e.activePayload[0].payload;
      setSelectedScan(clicked);
      setNote(clicked.stylistNotes || '');
    }
  };

  if (loading) return (
    <div style={fullPageLoader}>
      <div style={loaderSpin} />
      <p style={{ marginTop: '20px', letterSpacing: '4px', fontSize: '0.7rem', color: GOLD }}>
        INITIALIZING BIOMETRIC DATA...
      </p>
    </div>
  );

  const isAlert = selectedScan?.score < 40;

  return (
    <div style={container}>

      <header style={header}>
        <div>
          <h2 style={title}>PROFESSIONAL CONSULTATION DASHBOARD</h2>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem', margin: '4px 0 0', letterSpacing: '1px' }}>
            {history.length} session{history.length !== 1 ? 's' : ''} · Click any point on the chart to view that session
          </p>
        </div>
        <div style={accessBadge}>ACCESS LEVEL: 3D CRANIAL SOLID</div>
      </header>

      {/* LONGITUDINAL CHART */}
      <div style={chartCard}>
        <p style={sectionLabel}>FOLLICLE DENSITY TREND (%)</p>
        {history.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px', opacity:0.4 }}>
            <p>No biometric history recorded yet.</p>
          </div>
        ) : (
          <div style={{ height: 240, width: '100%' }}>
            <ResponsiveContainer>
              <LineChart data={history} onClick={handleChartClick} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" stroke="#333" fontSize={10} tickMargin={10} />
                <YAxis stroke="#333" fontSize={10} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={40} stroke="#ff4444" strokeDasharray="4 4"
                  label={{ value: 'ALERT THRESHOLD', fill: '#ff4444', fontSize: 9, position: 'right' }} />
                <Line type="monotone" dataKey="score" stroke={GOLD} strokeWidth={2.5}
                  dot={{ fill: GOLD, r: 5, strokeWidth: 0 }}
                  activeDot={{ r: 9, stroke: '#fff', strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* MAIN GRID */}
      <div style={mainGrid}>

        {/* 3D CRANIAL VIEWER */}
        <div style={threePanel}>
          <div style={panelHeader}>
            <p style={sectionLabel}>
              3D CRANIAL SOLID
              {selectedScan && <span style={{ color:'rgba(255,255,255,0.35)', fontWeight:'normal' }}> · {selectedScan.date}</span>}
            </p>
          </div>
          <div style={canvasBox}>
            <Canvas camera={{ position: [0, 0.5, 4] }}>
              <Suspense fallback={null}>
                <Stage environment="studio" intensity={0.6} adjustCamera={1.2}>
                  <ModelLoader
                    url={selectedScan?.headModelUrl || '/models/default-cranial-solid.glb'}
                    scale={1.8}
                    isCranial={true}
                  />
                </Stage>
                <OrbitControls enablePan={false} makeDefault />
              </Suspense>
            </Canvas>
          </div>
          <div style={cranialTag}>1:1 STRUCTURAL MESH</div>
        </div>

        {/* DIAGNOSTICS + NOTES */}
        <div style={rightPanel}>

          {/* Metrics */}
          <div style={metricsCard}>
            <p style={sectionLabel}>AI DIAGNOSTICS</p>

            <MetricRow label="Follicle Density" value={`${selectedScan?.score || 0}%`}
              alert={isAlert} />
            <MetricRow label="Hairline Score"
              value={selectedScan?.hv?.hairlineScore != null
                ? `${(selectedScan.hv.hairlineScore * 100).toFixed(1)}%` : 'N/A'} />
            <MetricRow label="Facial Symmetry"
              value={selectedScan?.hv?.facialSymmetry != null
                ? `${(selectedScan.hv.facialSymmetry * 100).toFixed(1)}%` : 'N/A'} />
            <MetricRow label="Temporal Width"
              value={selectedScan?.hv?.temporalWidth != null
                ? `${selectedScan.hv.temporalWidth.toFixed(1)} mm` : 'N/A'} />

            <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '12px',
              background: isAlert ? 'rgba(255,68,68,0.1)' : 'rgba(0,255,136,0.08)',
              border: `1px solid ${isAlert ? '#ff4444' : '#00ff88'}44` }}>
              <span style={{ fontSize: '0.65rem', fontWeight: '900', letterSpacing: '1px',
                color: isAlert ? '#ff4444' : '#00ff88' }}>
                {isAlert ? '⚠ CRITICAL — Thinning Pattern Detected' : '✓ STABLE — Normal Range'}
              </span>
            </div>
          </div>

          {/* Stylist notes */}
          <div style={notesCard}>
            <p style={sectionLabel}>STYLIST CONSULTATION NOTES</p>
            <textarea
              style={noteArea}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Record scalp observations, thinning patterns, product recommendations, treatment plan..."
            />
            <button onClick={handleSaveNote} disabled={isSaving} style={saveBtn}>
              {isSaving ? 'COMMITTING...' : 'COMMIT TO BIOMETRIC VAULT'}
            </button>
          </div>

          {/* Previous notes read-only */}
          {selectedScan?.stylistNotes && selectedScan.stylistNotes !== note && (
            <div style={prevNotesCard}>
              <p style={{ ...sectionLabel, marginBottom: '8px' }}>LAST SAVED NOTES</p>
              <p style={{ fontSize: '0.75rem', opacity: 0.6, lineHeight: 1.65, margin: 0 }}>
                {selectedScan.stylistNotes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Small reusable metric row ─────────────────────────────────────────────────
function MetricRow({ label, value, alert }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'14px', fontSize:'0.85rem', alignItems:'center' }}>
      <span style={{ opacity: 0.7 }}>{label}</span>
      <span style={{ color: alert ? '#ff4444' : GOLD, fontWeight: '900' }}>{value}</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const container      = { minHeight:'100vh', background:'#010810', color:'#fff', padding:'40px 32px', fontFamily:'Montserrat, sans-serif' };
const fullPageLoader = { height:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#010810' };
const loaderSpin     = { width:'36px', height:'36px', border:`2px solid ${GOLD}22`, borderTop:`2px solid ${GOLD}`, borderRadius:'50%', animation:'spin 1s linear infinite' };
const header         = { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'32px', flexWrap:'wrap', gap:'16px' };
const title          = { color:GOLD, fontSize:'1.3rem', letterSpacing:'2.5px', fontWeight:'900', margin:0 };
const accessBadge    = { fontSize:'0.58rem', background:`rgba(184,134,11,0.1)`, border:`1px solid ${GOLD}55`, padding:'6px 14px', borderRadius:'20px', color:GOLD, fontWeight:'bold', whiteSpace:'nowrap', alignSelf:'flex-start' };
const chartCard      = { background:'rgba(255,255,255,0.02)', padding:'28px', borderRadius:'28px', border:'1px solid rgba(255,255,255,0.05)', marginBottom:'28px' };
const mainGrid       = { display:'grid', gridTemplateColumns:'1fr 380px', gap:'28px' };
const threePanel     = { background:'#000', borderRadius:'28px', border:'1px solid rgba(255,255,255,0.05)', overflow:'hidden', display:'flex', flexDirection:'column', position:'relative', minHeight:'480px' };
const panelHeader    = { padding:'18px 22px', borderBottom:'1px solid rgba(255,255,255,0.05)' };
const canvasBox      = { flex:1, minHeight:'400px' };
const cranialTag     = { position:'absolute', bottom:'16px', left:'18px', fontSize:'0.55rem', color:GOLD, opacity:0.4, letterSpacing:'1.5px', fontWeight:'bold' };
const rightPanel     = { display:'flex', flexDirection:'column', gap:'20px' };
const metricsCard    = { background:'rgba(255,255,255,0.03)', padding:'24px', borderRadius:'24px', border:'1px solid rgba(255,255,255,0.05)' };
const notesCard      = { background:'rgba(255,255,255,0.02)', padding:'24px', borderRadius:'24px', border:`1px solid ${GOLD}33` };
const prevNotesCard  = { background:'rgba(184,134,11,0.04)', padding:'18px 22px', borderRadius:'20px', border:`1px solid ${GOLD}22` };
const noteArea       = { width:'100%', height:'160px', background:'#050a14', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'14px', color:'#fff', padding:'14px', fontSize:'0.82rem', outline:'none', resize:'none', marginBottom:'16px', boxSizing:'border-box', fontFamily:'Montserrat, sans-serif', lineHeight: 1.6 };
const saveBtn        = { width:'100%', background:GOLD, color:'#000', border:'none', padding:'14px', borderRadius:'14px', fontWeight:'900', cursor:'pointer', letterSpacing:'1px', fontSize:'0.75rem' };
const sectionLabel   = { fontSize:'0.62rem', color:GOLD, fontWeight:'900', letterSpacing:'2px', marginBottom:'16px', textTransform:'uppercase' };
